import React, {Component} from "react";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {DialogActions} from "@material-ui/core";
import {Button} from "react-bootstrap";
import {NumberInput, SelectInput} from "../../components";
import RemotingService from "../../services/remoting-service/RemotingService";
import NotificationService from "../../services/notification-service/NotificationService";
import confirmDialog from "../../components/dialog/ConfirmDialog";
import DateUtil from "../../utils/DateUtil";
import MoneyUtil from "../../utils/MoneyUtil";
import NumberUtils from "../../utils/NumberUtil";
import EnumBillTypeShort from "../patient/enums/EnumBillTypeShort";

const PAYMENT_TYPES = [
    {key: 'CASH', name: 'Cash', dueField: 'cashDue'},
    {key: 'CARD', name: 'Card', dueField: 'cardDue'},
    {key: 'ONLINE', name: 'Online', dueField: 'onlineDue'}
];
const AMOUNT_FIELDS = [
    {field: 'gross', label: 'GROSS'},
    {field: 'net', label: 'NET'},
    {field: 'coPay', label: 'CO PAY'},
    {field: 'credit', label: 'CREDIT'}
];
const moneyStyle = {textAlign: 'right'};

export default class InvoiceEditModal extends Component {

    constructor(props) {
        super(props);
        this.state = {
            view: null,
            items: [],
            saving: false
        };
    }

    componentDidMount() {
        RemotingService.getRemoteCall('api/billing/invoice/' + this.props.invoiceId + '/edit', null,
            view => this.loadView(view), () => this.props.close());
    }

    // The server view stays untouched as the original; inputs work on copies
    loadView(view) {
        this.setState({
            view,
            saving: false,
            items: view.invoice.items.map((item, index) => ({
                id: item.id,
                rowNo: index + 1,
                gross: item.gross || 0,
                net: item.net || 0,
                coPay: item.coPay || 0,
                credit: item.credit || 0,
                paymentType: null
            }))
        });
    }

    fractionDigits() {
        return MoneyUtil.getFractionDigitsWithCountry(this.state.view.invoice.clinic.country);
    }

    round(value) {
        return Number(Number(value || 0).toFixed(this.fractionDigits()));
    }

    money(value) {
        return NumberUtils.toFixed(value || 0, this.fractionDigits());
    }

    original(itemId) {
        return this.state.view.invoice.items.find(item => item.id === itemId);
    }

    // Item ids mean nothing to the user; rows are named by their number and code ("#1 70110")
    itemLabel(itemId) {
        if (itemId == null) {
            return '';
        }
        const item = this.state.items.find(candidate => candidate.id === Number(itemId));
        if (!item) {
            return String(itemId);
        }
        const code = this.original(item.id).code;
        return '#' + item.rowNo + (code ? ' ' + code : '');
    }

    // Self-pay patients pay the net, direct billing patients pay the co-pay
    trackedField(original) {
        return original.billType === 'SELF_PAY' ? 'net' : 'coPay';
    }

    // Same formula as the backend (InvoiceItem.getPatientPayable): tax and discounts are not editable here
    patientPayableOf(amounts, original) {
        if (original.billType === 'SELF_PAY') {
            return this.round(Number(amounts.net || 0) + Number(original.tax || 0));
        }
        return this.round(Number(amounts.coPay || 0) - Number(original.dwDiscount || 0) - Number(original.packageDiscount || 0));
    }

    patientPayable(item) {
        return this.patientPayableOf(item, this.original(item.id));
    }

    patientPayableDifference(item) {
        const original = this.original(item.id);
        return this.round(this.patientPayable(item) - this.patientPayableOf(original, original));
    }

    isPaymentTypeRequired(item) {
        return !!this.state.view.invoice.billId
            && NumberUtils.isNumeric(item[this.trackedField(this.original(item.id))])
            && this.patientPayableDifference(item) !== 0;
    }

    handleItemChange(itemId, field, value) {
        this.setState(state => ({
            items: state.items.map(item => item.id === itemId ? {...item, [field]: value} : item)
        }));
    }

    amountChanges() {
        const changes = [];
        this.state.items.forEach(item => {
            const original = this.original(item.id);
            AMOUNT_FIELDS.forEach(({field, label}) => {
                const difference = this.round(Number(item[field]) - Number(original[field] || 0));
                if (difference !== 0) {
                    changes.push({itemId: item.id, label, original: original[field] || 0, value: item[field], difference});
                }
            });
        });
        return changes;
    }

    billChanges() {
        return PAYMENT_TYPES.map(paymentType => {
            const original = Number(this.state.view[paymentType.dueField] || 0);
            const difference = this.round(this.state.items
                .filter(item => item.paymentType === paymentType.key && this.isPaymentTypeRequired(item))
                .reduce((total, item) => total + this.patientPayableDifference(item), 0));
            return {name: paymentType.name, original, value: this.round(original + difference), difference};
        }).filter(change => change.difference !== 0);
    }

    save = () => {
        const {items} = this.state;

        const invalidItems = items.filter(item =>
            AMOUNT_FIELDS.some(({field}) => !NumberUtils.isNumeric(item[field]) || Number(item[field]) < 0));
        if (invalidItems.length > 0) {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Incorrect Values',
                detail: 'Amounts of item(s) ' + invalidItems.map(item => this.itemLabel(item.id)).join(', ')
                    + ' must be 0 or more.'
            });
            return;
        }

        const missingPaymentTypes = items.filter(item => this.isPaymentTypeRequired(item) && !item.paymentType);
        if (missingPaymentTypes.length > 0) {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Payment Type Required',
                detail: 'The amount paid by the patient changes for item(s) '
                    + missingPaymentTypes.map(item => this.itemLabel(item.id)).join(', ')
                    + '; select the payment type the patient paid with.'
            });
            return;
        }

        const amountChanges = this.amountChanges();
        if (amountChanges.length === 0) {
            NotificationService.showNotification({severity: 'warn', summary: 'No Changes', detail: 'Nothing to save.'});
            return;
        }

        confirmDialog("Confirm Financial Changes",
            "Please review the financial changes below and double-check all calculations before saving.",
            () => this.submit(), null,
            this.renderChangeSummary(amountChanges, this.billChanges()), "Confirm & Save");
    }

    submit() {
        const dto = {
            items: this.state.items.map(item => ({
                id: item.id,
                gross: item.gross,
                net: item.net,
                coPay: item.coPay,
                credit: item.credit,
                paymentType: this.isPaymentTypeRequired(item) ? item.paymentType : null
            }))
        };

        this.setState({saving: true});
        RemotingService.postRemoteCall('api/billing/invoice/' + this.props.invoiceId + '/edit', dto,
            view => {
                if (view.invoice.pdfPath) {
                    NotificationService.showNotification({
                        severity: 'warn',
                        summary: 'Invoice PDF',
                        detail: 'The stored invoice PDF still shows the old amounts. Use Regenerate Invoice to refresh it.'
                    });
                }
                if (view.odooReminder) {
                    NotificationService.showNotification({
                        severity: 'warn',
                        summary: 'Odoo',
                        detail: 'Please do not forget to check this invoice with id ' + view.invoice.id
                            + ' on Odoo and update if necessary.'
                    });
                }
                this.loadView(view);
                this.props.onSaved();
            },
            () => this.setState({saving: false}));
    }

    differenceTemplate(difference) {
        return <span className={difference >= 0 ? 'text-success' : 'text-danger'}>
            {(difference >= 0 ? '+' : '') + this.money(difference)}
        </span>;
    }

    renderChangeSummary(amountChanges, billChanges) {
        return (
            <div style={{width: '600px'}} className="mt-3">
                <h6>Invoice Item Changes</h6>
                <table className="table table-sm">
                    <thead>
                    <tr><th>Item</th><th>Field</th><th>Original</th><th>New</th><th>Change</th></tr>
                    </thead>
                    <tbody>
                    {amountChanges.map(change =>
                        <tr key={change.itemId + change.label}>
                            <td>{this.itemLabel(change.itemId)}</td>
                            <td>{change.label}</td>
                            <td>{this.money(change.original)}</td>
                            <td>{this.money(change.value)}</td>
                            <td>{this.differenceTemplate(change.difference)}</td>
                        </tr>)}
                    </tbody>
                </table>
                {billChanges.length > 0 &&
                <>
                    <h6>Bill Payment Impact</h6>
                    <table className="table table-sm">
                        <thead>
                        <tr><th>Payment Method</th><th>Original</th><th>New</th><th>Change</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                        {billChanges.map(change =>
                            <tr key={change.name}>
                                <td>{change.name}</td>
                                <td>{this.money(change.original)}</td>
                                <td>{this.money(change.value)}</td>
                                <td>{this.differenceTemplate(change.difference)}</td>
                                <td>{change.value !== 0 ? 'Enabled' : 'Disabled'}</td>
                            </tr>)}
                        </tbody>
                    </table>
                    {billChanges.filter(change => change.value < 0).map(change =>
                        <div key={'refund-' + change.name} className="alert alert-danger mb-2">
                            {change.name} payment will be {this.money(change.value)}: more was received than the
                            patient has to pay, so this amount is to be refunded to the patient.
                        </div>)}
                </>}
            </div>
        );
    }

    amountTemplate(item, field) {
        return <NumberInput value={item[field]} style={{width: '100%'}} allowDecimal={true}
                            maxFractionDigits={this.fractionDigits()}
                            onChange={value => this.handleItemChange(item.id, field, value)}/>;
    }

    readOnlyAmountTemplate(item, field) {
        return this.money(this.original(item.id)[field]);
    }

    patientPayableTemplate(item) {
        const changed = this.patientPayableDifference(item) !== 0;
        return <span className={changed ? 'font-weight-bold' : ''}>{this.money(this.patientPayable(item))}</span>;
    }

    paymentTypeTemplate(item) {
        return <SelectInput displayProperty="name" valueProperty="key" searchEnabled={false}
                            placeholder="Payment Type"
                            disabled={!this.isPaymentTypeRequired(item)}
                            value={item.paymentType}
                            options={PAYMENT_TYPES}
                            onChange={value => this.handleItemChange(item.id, 'paymentType', value)}/>;
    }

    renderHeader() {
        const {invoice, patientEid, claimGenerated, claimStatus} = this.state.view;
        return (
            <>
                <div className="row">
                    <div className="col-6">
                        <div><b>Clinic: </b>{invoice.clinic?.name}</div>
                        <div><b>Patient: </b>{invoice.patientName} <i>(<b>MRN</b> {invoice.patientMrn}) (<b>EID</b> {patientEid})</i></div>
                        <div><b>Provider: </b>{invoice.providerName}</div>
                    </div>
                    <div className="col-6">
                        <div><b>Invoice Date: </b>{DateUtil.formatDateMMM(invoice.invoiceDate)}</div>
                        <div><b>Bill Type: </b>{EnumBillTypeShort[invoice.billType] || invoice.billType}</div>
                        <div><b>Status: </b>{invoice.status}</div>
                    </div>
                </div>
                {claimGenerated &&
                <div className="alert alert-warning mt-2 mb-0">
                    A claim{claimStatus ? ' (' + claimStatus + ')' : ''} was already generated from this invoice. The claim keeps
                    its own net and prior authorization values; correct those in the Claims tab.
                </div>}
            </>
        );
    }

    render() {
        if (!this.state.view) {
            return null;
        }
        const history = this.state.view.history || [];

        return (
            <>
                <div className="container padding-10" style={{width: '100%', maxWidth: '100%'}}>
                    {this.renderHeader()}
                    <h6 className="mt-3">Invoice Items</h6>
                    <DataTable responsive autoLayout value={this.state.items} dataKey="id">
                        <Column header="#" field="rowNo" style={{width: '45px'}}/>
                        <Column header="Code" body={item => this.original(item.id).code}/>
                        <Column header="Description" body={item => this.original(item.id).description}
                                style={{minWidth: '220px'}}/>
                        <Column header="Qty" body={item => this.original(item.id).quantity} style={{width: '50px'}}/>
                        <Column header="Gross" body={item => this.amountTemplate(item, 'gross')} style={{width: '90px'}}/>
                        <Column header="Net" body={item => this.amountTemplate(item, 'net')} style={{width: '90px'}}/>
                        <Column header="Co-Pay" body={item => this.amountTemplate(item, 'coPay')} style={{width: '90px'}}/>
                        <Column header="Sponsorship" body={item => this.readOnlyAmountTemplate(item, 'sponsorship')}
                                bodyStyle={moneyStyle}/>
                        <Column header="Tax" body={item => this.readOnlyAmountTemplate(item, 'tax')}
                                bodyStyle={moneyStyle}/>
                        <Column header="DW Discount" body={item => this.readOnlyAmountTemplate(item, 'dwDiscount')}
                                bodyStyle={moneyStyle}/>
                        <Column header="Package Discount" body={item => this.readOnlyAmountTemplate(item, 'packageDiscount')}
                                bodyStyle={moneyStyle}/>
                        <Column header="Credit" body={item => this.amountTemplate(item, 'credit')} style={{width: '90px'}}/>
                        <Column header="Patient Payable" body={item => this.patientPayableTemplate(item)}
                                bodyStyle={moneyStyle}/>
                        <Column header="Payment Type" body={item => this.paymentTypeTemplate(item)} style={{width: '140px'}}/>
                    </DataTable>
                    <small className="text-muted">
                        Self-pay: changing Net changes what the patient pays, so choose the payment type the patient paid with
                        (a lower amount is recorded as a refund on that payment type). Direct billing: the same applies to Co-Pay;
                        Gross and Net changes never touch the bill.
                    </small>
                    <h6 className="mt-3">Edit History</h6>
                    <DataTable responsive emptyMessage="No edits yet" value={history}
                               paginator={history.length > 10} rows={10}>
                        <Column header="Date" body={log => DateUtil.formatDateTime12H(log.date)}/>
                        <Column header="User" field="user"/>
                        <Column header="Change Type" field="type"/>
                        <Column header="Old Value" field="oldValue"/>
                        <Column header="New Value" field="newValue"/>
                        <Column header="Item" body={log => this.itemLabel(log.itemId)}/>
                    </DataTable>
                </div>
                <DialogActions>
                    <Button variant="success" disabled={this.state.saving} onClick={this.save}>Save Invoice</Button>
                </DialogActions>
            </>
        );
    }
}
